/**
 * Study Mode Features component - Interactive tabs like Claude Code
 */

import {useState} from 'react';
import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface Feature {
  id: string;
  title: string;
  description: string;
  detail: string;
  example?: {
    prompt: string;
    response: string;
  };
}

const features: Feature[] = [
  {
    id: 'upload-analyze',
    title: 'Upload & Analyze',
    description: 'Upload any study material - PDFs, images, handwritten notes. Marki reads everything perfectly.',
    detail: 'Transcribes text, crops images, handles messy work. No markscheme? We generate one for you. Extract questions and answers from any format instantly.',
    example: {
      prompt: '> Upload my biology lecture notes and extract key concepts',
      response: '• I\'ll analyze your biology notes and extract all key concepts, definitions, and important information.\n• Analyze(what: "Extract key concepts, definitions, formulas, and important facts from the uploaded biology lecture notes. Organize them by topic and highlight connections between concepts.")',
    },
  },
  {
    id: 'chat-tutor',
    title: 'Chat with AI Tutor',
    description: 'Ask questions, get explanations, and receive personalized guidance.',
    detail: 'The AI remembers all your uploaded materials and study progress. Get context-aware answers that reference specific sections of your documents.',
    example: {
      prompt: '> Explain photosynthesis in simple terms',
      response: '• Photosynthesis is the process plants use to convert light energy into chemical energy.\n• Based on your notes, here\'s how it relates to the concepts you\'ve already learned...',
    },
  },
  {
    id: 'generate-questions',
    title: 'Generate Study Materials',
    description: 'Create flashcards, practice questions, and quizzes instantly.',
    detail: 'Generate personalized study materials on demand. Get flashcards, multiple-choice questions, and practice problems tailored to your content and difficulty level.',
    example: {
      prompt: '> Create 20 flashcards from my chemistry notes',
      response: '• I\'ll generate 20 flashcards covering key chemistry concepts from your notes.\n• Generate(what: "Create flashcards with questions on one side and answers on the other, covering atomic structure, chemical bonds, and reactions from the uploaded notes.")',
    },
  },
];

export function StudyFeatures() {
  const {ref, isVisible} = useScrollAnimation();
  const [activeFeature, setActiveFeature] = useState(features[0].id);

  const activeFeatureData = features.find((f) => f.id === activeFeature) || features[0];

  return (
    <Section id="features" padding="lg" background="white" ref={ref} className="bg-background dark:bg-[#1E1E1E]">
      <Container>
        <div className={`transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
              What could you do with Study Mode?
            </h2>
            <p className="text-lg text-textSecondary dark:text-[#858585] max-w-2xl mx-auto">
              Powerful features that transform how you study
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Feature Tabs - Connected Style */}
            <div className="flex items-center justify-center mb-8">
              <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 bg-background dark:bg-[#1E1E1E] p-1">
                {features.map((feature) => (
                  <button
                    key={feature.id}
                    onClick={() => setActiveFeature(feature.id)}
                    className={`px-6 py-2.5 text-sm font-medium transition-all relative ${
                      activeFeature === feature.id
                        ? 'bg-backgroundSecondary dark:bg-[#252526] text-textPrimary dark:text-[#CCCCCC] rounded-md border border-orange-500/30'
                        : 'text-textSecondary dark:text-[#858585] hover:text-textPrimary dark:hover:text-[#CCCCCC] hover:text-orange-600/70 dark:hover:text-orange-500/70'
                    }`}
                  >
                    {feature.title}
                  </button>
                ))}
              </div>
            </div>

            {/* Feature Content - Split Layout */}
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Left: Feature Description */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-3">
                    {activeFeatureData.title}
                  </h3>
                  <p className="text-lg text-textSecondary dark:text-[#858585] leading-relaxed mb-4">
                    {activeFeatureData.description}
                  </p>
                  <p className="text-base text-textTertiary dark:text-[#6A6A6A] leading-relaxed">
                    {activeFeatureData.detail}
                  </p>
                </div>
              </div>

              {/* Right: Interactive Demo Preview */}
              <div className="bg-backgroundSecondary dark:bg-[#252526] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                {activeFeatureData.example && (
                  <div className="space-y-4">
                    {/* Prompt */}
                    <div className="space-y-2">
                      <div className="text-xs text-textTertiary dark:text-[#6A6A6A] mb-2">User prompt:</div>
                      <div className="font-mono text-sm text-textPrimary dark:text-[#CCCCCC] bg-background dark:bg-[#1E1E1E] rounded p-3 border border-gray-200 dark:border-gray-700">
                        <span className="text-orange-600/80 dark:text-orange-500/80">{'>'}</span>
                        {activeFeatureData.example.prompt.slice(1)}
                      </div>
                    </div>

                    {/* Response */}
                    <div className="space-y-2">
                      <div className="text-xs text-textTertiary dark:text-[#6A6A6A] mb-2">AI response:</div>
                      <div className="font-mono text-sm text-textSecondary dark:text-[#858585] bg-background dark:bg-[#1E1E1E] rounded p-3 border border-gray-200 dark:border-gray-700">
                        {activeFeatureData.example.response.split('\n').map((line, idx) => {
                          if (line.includes('Analyze(') || line.includes('Generate(')) {
                            const parts = line.split('(');
                            return (
                              <div key={idx} className="mb-1">
                                {parts[0]}(
                                <span className="text-orange-600/70 dark:text-orange-500/70">{parts.slice(1).join('(')}</span>
                              </div>
                            );
                          }
                          return <div key={idx} className="mb-1">{line}</div>;
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}

